import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit2, X, Save, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import apiClient from '../api/client';

const ProductManager = () => {
  const { showToast } = useToast();
  const [products, setProducts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: 0,
    category: 'Choripanes',
    ingredients: [] as any[],
    options: [] as any[]
  });

  const fetchProducts = useCallback(async () => {
    try {
      const res = await apiClient.get('/products');
      setProducts(res.data);
    } catch (error) {
      showToast('Error al cargar productos', 'error');
    }
  }, [showToast]);

  const fetchInventory = useCallback(async () => {
    try {
      const res = await apiClient.get('/inventory');
      setInventory(res.data);
    } catch (error) {
      showToast('Error al cargar inventario', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchProducts();
    fetchInventory();
  }, [fetchProducts, fetchInventory]);

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...product,
        ingredients: product.ingredients?.map((i: any) => ({
          inventoryItem: i.inventoryItem._id || i.inventoryItem,
          quantity: i.quantity
        })) || [],
        options: (product as any).options?.map((opt: any) => ({
          name: opt.name || '',
          displayStyle: opt.displayStyle || 'pills',
          choices: (opt.choices || []).map((choice: string) => ({
            value: choice,
            description: opt.descriptions?.[choice] || '',
            icon: opt.icons?.[choice] || ''
          }))
        })) || []
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: 0,
        category: 'Choripanes',
        ingredients: [],
        options: []
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        options: formData.options
          .filter(opt => opt.name.trim() && opt.choices.some((c: any) => c.value.trim()))
          .map(opt => {
            const choices = opt.choices.filter((c: any) => c.value.trim());
            const descriptions: Record<string, string> = {};
            const icons: Record<string, string> = {};
            choices.forEach((c: any) => {
              if (c.description.trim()) descriptions[c.value] = c.description;
              if (c.icon.trim()) icons[c.value] = c.icon;
            });
            return {
              name: opt.name,
              displayStyle: opt.displayStyle,
              choices: choices.map((c: any) => c.value),
              descriptions,
              icons
            };
          })
      };
      if (editingProduct) {
        await apiClient.put(`/products/${editingProduct._id}`, payload);
        showToast('Producto actualizado', 'success');
      } else {
        await apiClient.post('/products', payload);
        showToast('Producto creado', 'success');
      }
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      showToast('Error al guardar producto', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('¿Estás seguro de eliminar este producto?')) {
      try {
        await apiClient.delete(`/products/${id}`);
        showToast('Producto eliminado', 'success');
        fetchProducts();
      } catch (error) {
        showToast('Error al eliminar producto', 'error');
      }
    }
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { inventoryItem: '', quantity: 1 }]
    });
  };

  const removeIngredient = (index: number) => {
    const newIngredients = [...formData.ingredients];
    newIngredients.splice(index, 1);
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const updateIngredient = (index: number, field: string, value: any) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData({ ...formData, ingredients: newIngredients });
  };

  const addOption = () => {
    setFormData({
      ...formData,
      options: [...formData.options, { name: '', displayStyle: 'pills', choices: [] as any[] }]
    });
  };

  const removeOption = (index: number) => {
    const newOptions = [...formData.options];
    newOptions.splice(index, 1);
    setFormData({ ...formData, options: newOptions });
  };

  const updateOption = (index: number, field: string, value: any) => {
    const newOptions = [...formData.options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, options: newOptions });
  };

  const addChoice = (optionIndex: number) => {
    const newOptions = [...formData.options];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      choices: [...newOptions[optionIndex].choices, { value: '', description: '', icon: '' }]
    };
    setFormData({ ...formData, options: newOptions });
  };

  const removeChoice = (optionIndex: number, choiceIndex: number) => {
    const newOptions = [...formData.options];
    const newChoices = [...newOptions[optionIndex].choices];
    newChoices.splice(choiceIndex, 1);
    newOptions[optionIndex] = { ...newOptions[optionIndex], choices: newChoices };
    setFormData({ ...formData, options: newOptions });
  };

  const updateChoice = (optionIndex: number, choiceIndex: number, field: string, value: any) => {
    const newOptions = [...formData.options];
    const newChoices = [...newOptions[optionIndex].choices];
    newChoices[choiceIndex] = { ...newChoices[choiceIndex], [field]: value };
    newOptions[optionIndex] = { ...newOptions[optionIndex], choices: newChoices };
    setFormData({ ...formData, options: newOptions });
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Package className="text-primary" />
          Gestión de Productos
        </h2>
        <Button onClick={() => handleOpenModal()} className="rounded-xl flex items-center gap-2">
          <Plus size={20} />
          Nuevo Producto
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product: any) => (
          <Card key={product._id} className="p-6 group relative overflow-hidden">
            <div className="flex justify-between items-start mb-4">
              <div>
                <span className="text-[10px] font-black uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-md">
                  {product.category}
                </span>
                <h3 className="text-xl font-black mt-2">{product.name}</h3>
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => handleOpenModal(product)} className="h-8 w-8 rounded-lg bg-slate-100 dark:bg-slate-800">
                  <Edit2 size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(product._id)} className="h-8 w-8 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            <p className="text-slate-500 text-sm line-clamp-2 mb-4">{product.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-2xl font-black text-slate-900 dark:text-white">
                ${product.price.toLocaleString()}
              </span>
              <div className="text-[10px] font-bold text-slate-400 uppercase">
                {product.ingredients?.length || 0} Ingredientes
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-950 w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-3xl font-black">
                    {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
                  </h2>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setIsModalOpen(false)}>
                    <X size={24} />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-500 uppercase">Nombre</label>
                      <input 
                        required
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-500 uppercase">Precio</label>
                      <input 
                        required
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.price}
                        onChange={e => setFormData({...formData, price: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-500 uppercase">Descripción</label>
                    <textarea 
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20 min-h-[100px]"
                      value={formData.description}
                      onChange={e => setFormData({...formData, description: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-bold text-slate-500 uppercase">Categoría</label>
                    <select 
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                    >
                      <option value="Choripanes">Choripanes</option>
                      <option value="Combos">Combos</option>
                      <option value="Bebidas">Bebidas</option>
                      <option value="Acompañamientos">Acompañamientos</option>
                    </select>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-bold text-slate-500 uppercase">Ingredientes (Receta)</label>
                      <Button type="button" variant="outline" size="sm" onClick={addIngredient} className="rounded-xl">
                        <Plus size={16} className="mr-2" /> Añadir Insumo
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.ingredients.map((ing, index) => (
                        <div key={index} className="flex gap-3 items-center bg-slate-50 dark:bg-slate-900/50 p-3 rounded-2xl">
                          <select 
                            required
                            className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                            value={ing.inventoryItem}
                            onChange={e => updateIngredient(index, 'inventoryItem', e.target.value)}
                          >
                            <option value="">Seleccionar insumo...</option>
                            {inventory.map((item: any) => (
                              <option key={item._id} value={item._id}>{item.name} ({item.unit})</option>
                            ))}
                          </select>
                          <input 
                            type="number"
                            step="0.1"
                            placeholder="Cant."
                            className="w-20 bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                            value={ing.quantity}
                            onChange={e => updateIngredient(index, 'quantity', parseFloat(e.target.value))}
                          />
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeIngredient(index)} className="text-red-500">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-bold text-slate-500 uppercase">Opciones (Sabores, Tamaños, etc.)</label>
                      <Button type="button" variant="outline" size="sm" onClick={addOption} className="rounded-xl">
                        <Plus size={16} className="mr-2" /> Añadir Opción
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {formData.options.map((opt, optIndex) => (
                        <div key={optIndex} className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-2xl space-y-3">
                          <div className="flex gap-3 items-center">
                            <input
                              placeholder="Nombre de la opción (ej. Sabor)"
                              className="flex-1 bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                              value={opt.name}
                              onChange={e => updateOption(optIndex, 'name', e.target.value)}
                            />
                            <select
                              className="bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                              value={opt.displayStyle}
                              onChange={e => updateOption(optIndex, 'displayStyle', e.target.value)}
                            >
                              <option value="pills">Pills</option>
                              <option value="grid">Grid</option>
                            </select>
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeOption(optIndex)} className="text-red-500">
                              <Trash2 size={16} />
                            </Button>
                          </div>

                          <div className="space-y-2 pl-2 border-l-2 border-slate-200 dark:border-slate-700">
                            {opt.choices.map((choice: any, choiceIndex: number) => (
                              <div key={choiceIndex} className="flex gap-2 items-center flex-wrap">
                                <input
                                  placeholder="Valor (ej. Argentino)"
                                  className="flex-1 min-w-[120px] bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                                  value={choice.value}
                                  onChange={e => updateChoice(optIndex, choiceIndex, 'value', e.target.value)}
                                />
                                <input
                                  placeholder="Descripción (opcional)"
                                  className="flex-1 min-w-[120px] bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                                  value={choice.description}
                                  onChange={e => updateChoice(optIndex, choiceIndex, 'description', e.target.value)}
                                />
                                <input
                                  placeholder="Ícono/emoji (opcional)"
                                  className="w-32 bg-white dark:bg-slate-800 border-none rounded-xl py-2 px-3 text-sm"
                                  value={choice.icon}
                                  onChange={e => updateChoice(optIndex, choiceIndex, 'icon', e.target.value)}
                                />
                                <Button type="button" variant="ghost" size="icon" onClick={() => removeChoice(optIndex, choiceIndex)} className="text-red-500">
                                  <X size={16} />
                                </Button>
                              </div>
                            ))}
                            <Button type="button" variant="outline" size="sm" onClick={() => addChoice(optIndex)} className="rounded-xl">
                              <Plus size={14} className="mr-2" /> Añadir Valor
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-16 rounded-2xl text-lg font-black mt-8 flex items-center justify-center gap-2">
                    <Save size={20} />
                    {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductManager;
